import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Navigation from '../components/Navigation';
import { getAdminHeaders } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminWhatsApp = () => {
  const navigate = useNavigate();
  const [links, setLinks] = useState({ pais_raizes: '', equipe: '', groups: {} });
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/groups/whatsapp-links/admin`, { headers: getAdminHeaders() });
      setLinks(res.data.links || { pais_raizes: '', equipe: '', groups: {} });
      setGroups(res.data.available_groups || []);
    } catch { toast.error('Erro ao carregar links'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/groups/whatsapp-links`, links, { headers: getAdminHeaders() });
      toast.success('Links atualizados com sucesso!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#25D366] text-white px-4 sm:px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <MessageCircle className="w-7 h-7" />
            <div>
              <h1 className="font-nunito font-bold text-2xl sm:text-3xl">Links de WhatsApp</h1>
              <p className="font-inter text-white/80 text-sm mt-1">Gerencie os links dos grupos</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Pais Raizes */}
        <Card className="bg-white rounded-2xl p-5 shadow-sm border border-[#E1ECE5]">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-[#25D366]/10"><MessageCircle className="w-4 h-4 text-[#25D366]" /></div>
            <Label className="font-nunito font-bold text-base text-[#1A4D2E]">Grupo Pais Raizes</Label>
          </div>
          <p className="font-inter text-xs text-gray-500 mb-2">Link exibido para todos os pais no portal</p>
          <Input
            data-testid="whatsapp-pais-raizes"
            value={links.pais_raizes || ''}
            onChange={e => setLinks(prev => ({ ...prev, pais_raizes: e.target.value }))}
            placeholder="https://chat.whatsapp.com/..."
            className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#25D366]"
          />
        </Card>

        {/* Equipe */}
        <Card className="bg-white rounded-2xl p-5 shadow-sm border border-[#E1ECE5]">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-[#1A4D2E]/10"><MessageCircle className="w-4 h-4 text-[#1A4D2E]" /></div>
            <Label className="font-nunito font-bold text-base text-[#1A4D2E]">Grupo da Equipe</Label>
          </div>
          <p className="font-inter text-xs text-gray-500 mb-2">Link exibido no painel dos voluntarios</p>
          <Input
            data-testid="whatsapp-equipe"
            value={links.equipe || ''}
            onChange={e => setLinks(prev => ({ ...prev, equipe: e.target.value }))}
            placeholder="https://chat.whatsapp.com/..."
            className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#25D366]"
          />
        </Card>

        {/* GFs */}
        <Card className="bg-white rounded-2xl p-5 shadow-sm border border-[#E1ECE5]">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-amber-100"><MessageCircle className="w-4 h-4 text-amber-700" /></div>
            <Label className="font-nunito font-bold text-base text-[#1A4D2E]">Grupos de Discipulado (GFs)</Label>
          </div>
          <p className="font-inter text-xs text-gray-500 mb-3">Links exibidos para os pais conforme o GF do filho</p>
          <div className="space-y-3">
            {groups.map(g => (
              <div key={g.id}>
                <Label className="font-inter text-sm text-gray-700 mb-1 block">{g.nome}</Label>
                <Input
                  data-testid={`whatsapp-group-${g.id}`}
                  value={(links.groups || {})[g.id] || ''}
                  onChange={e => setLinks(prev => ({
                    ...prev,
                    groups: { ...(prev.groups || {}), [g.id]: e.target.value }
                  }))}
                  placeholder="https://chat.whatsapp.com/..."
                  className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#25D366] text-sm"
                />
              </div>
            ))}
          </div>
        </Card>

        {/* Save */}
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-whatsapp-links"
          className="w-full bg-[#25D366] hover:bg-[#20bd5a] disabled:opacity-50 text-white rounded-2xl py-4 text-base font-bold shadow-md flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Salvando...' : 'Salvar Links'}
        </button>
      </div>

      <Navigation active="" />
    </div>
  );
};

export default AdminWhatsApp;
